import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CheckCircle,
  CircleDot,
  DollarSign,
  Eye,
  FileText,
  Handshake,
  Heart,
  KeyRound,
  LayoutGrid,
  List,
  Lock,
  MapPin,
  Search,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  X,
  Zap,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/toast";
import { api, assetUrl, ApiError } from "@/lib/api";
import { isFreeBuyerUser } from "@/lib/access";
import { cn, getListingHotBadge } from "@/lib/utils";
import { Pagination } from "@/components/ui/pagination";
import ListingHotBadge from "@/components/ListingHotBadge";
import { TrendingSection, type TrendingListing } from "@/components/TrendingCarousel";
import { ListingFilters, buildListingFilterParams, readStoredFilters, EMPTY_FILTERS, type ListingFilterValues } from "@/components/ListingFilters";
import type { BusinessListing } from "@/types";
import type { ListingMapPoint } from "@/components/ListingsMap";
import { MarketExplorer } from "@/components/MarketExplorer";
import { loadMapPoints } from "@/lib/mapPoints";
import { ComingSoonDeals } from "@/components/ComingSoonDeals";
import { CategoryTiles } from "@/components/CategoryTiles";
import { UnlockListingModal } from "@/components/UnlockListingModal";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import PublicLanding from "@/components/PublicLanding";
import { HeroCarousel, featureBanners } from "@/components/HeroCarousel";
import { useModuleVisibility } from "@/lib/moduleVisibility";

interface LookupItem {
  id: number;
  name: string;
}

/** "Thursday, Oct 8" style label for when the next Basic unlock batch arrives
 *  (UTC midnight, shown in the viewer's locale). */
const formatResetDay = (iso: string) =>
  `on ${new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}`;

const GRID_PAGE_SIZE = 12;
const TABLE_PAGE_SIZE = 20;

// Remember the buyer's current listings page for this browser session so that
// returning from a listing detail (Back to listings) lands on the same page.
const LISTINGS_PAGE_KEY = "buyerListingsPage";
// Remember the applied filters for this session too, so Back to listings
// restores the filtered view, not just the page.
const LISTINGS_FILTERS_KEY = "buyerListingsFilters";
function readSavedListingsPage(): number {
  try {
    const v = Number(sessionStorage.getItem(LISTINGS_PAGE_KEY));
    return Number.isFinite(v) && v >= 1 ? v : 1;
  } catch {
    return 1;
  }
}

const fmtNum = (n: number): string => {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(n % 1_000_000_000 === 0 ? 0 : 1)}b`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}m`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`;
  return String(n);
};

const fmtCompact = (n: number | null): string => n ? `$${fmtNum(n)}` : "—";

/**
 * Compact currency for the hero portfolio stat: whole millions below $1B
 * (e.g. $825M), rolling over to one-decimal billions at/above it (e.g. $1.9B).
 */
const fmtMillions = (n: number): string => {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(n % 1_000_000_000 === 0 ? 0 : 1)}B`;
  return `$${Math.round(n / 1_000_000).toLocaleString()}M`;
};

// Status KPI tabs shown above the buyer marketplace filters. Clicking one
// filters the listings to that status (ids match the backend listing_statuses
// table). `active` (3) is the default view. Colours mirror AdminAllListings.
// (Class strings are kept literal so Tailwind can detect them.)
const BUYER_STATUS_KPIS: {
  id: number;
  label: string;
  icon: typeof CircleDot;
  chip: string;    // icon chip background + colour
  bar: string;     // left accent bar colour
  num: string;     // number colour when selected
  selBg: string;   // card tint when selected
  selRing: string; // ring colour when selected
}[] = [
  { id: 3,  label: "Active",        icon: CircleDot,   chip: "bg-emerald-100 text-emerald-600", bar: "bg-emerald-500", num: "text-emerald-700", selBg: "bg-emerald-50/70", selRing: "ring-emerald-300/70" },
  { id: 9,  label: "LOI",           icon: FileText,    chip: "bg-purple-100 text-purple-600",   bar: "bg-purple-500",  num: "text-purple-700",  selBg: "bg-purple-50/70",  selRing: "ring-purple-300/70" },
  { id: 4,  label: "Under Offer",   icon: Handshake,   chip: "bg-cyan-100 text-cyan-600",       bar: "bg-cyan-500",    num: "text-cyan-700",    selBg: "bg-cyan-50/70",    selRing: "ring-cyan-300/70" },
  { id: 10, label: "Due Diligence", icon: ShieldCheck, chip: "bg-indigo-100 text-indigo-600",   bar: "bg-indigo-500",  num: "text-indigo-700",  selBg: "bg-indigo-50/70",  selRing: "ring-indigo-300/70" },
  { id: 5,  label: "Sold",          icon: BadgeCheck,  chip: "bg-sky-100 text-sky-600",         bar: "bg-sky-500",     num: "text-sky-700",     selBg: "bg-sky-50/70",     selRing: "ring-sky-300/70" },
];

export default function HomePage() {
  const { user, hasRole } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminOrAgent = !!user && (hasRole("admin") || hasRole("bl_advisor"));
  const isOperator = hasRole("seller", "bl_advisor", "admin");
  // Buyers, advisors, and admins view the home page inside a left sidebar shell, so let content fill the wider column.
  const isBuyerView = hasRole("buyer") && !hasRole("admin");
  // Every visitor (including logged-out) gets the full-width hero — no centered max-w column.
  const shellWidth = "w-full";

  const [listings, setListings] = useState<BusinessListing[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // Buyer marketplace status KPI: which listing status the buyer is browsing.
  // Defaults to Active (3); other KPIs reveal LOI / Under Offer / Due Diligence / Sold.
  const [statusKpi, setStatusKpi] = useState<number>(3);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  // Summed revenue across live active listings — the hero's portfolio stat.
  const [activeRevenueTotal, setActiveRevenueTotal] = useState<number | null>(null);
  // Marketplace tab: live listings vs the coming-soon deal pipeline. The
  // shared search/filter bar and grid/table toggle apply to whichever tab is
  // open. Deep-linkable via ?tab=coming-soon (e.g. from My Interests) and
  // ?tab=unlocks. "unlocks" is the buyer's own unlocked deals — same grid,
  // fetched from /business-listings/?unlocked_only=true.
  const [marketTab, setMarketTab] = useState<"active" | "coming_soon" | "unlocks">(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return t === "coming-soon" ? "coming_soon" : t === "unlocks" ? "unlocks" : "active";
  });
  const [comingSoonTotal, setComingSoonTotal] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [currentPage, setCurrentPage] = useState(() => readSavedListingsPage());
  // Page to load on the very first fetch (restored from the session). Later,
  // only an actual filter/sort/view change resets back to page 1.
  const initialPageRef = useRef(readSavedListingsPage());
  const didInitialFetchRef = useRef(false);
  const lastBrowseSigRef = useRef<string>("");
  // "Available listings" section, so paging scrolls there (not the whole page).
  const marketplaceRef = useRef<HTMLElement>(null);
  // Skip the auto-scroll on the very first fetch so the page doesn't jump past
  // the hero/trending on initial load.
  const skipScrollRef = useRef(true);
  // ...except when the buyer is coming *back* from a listing. Restoring page 61
  // but leaving the viewport on the hero reads as "this is page 1 again", so
  // they navigate away and lose their place. Two signals, because both routes
  // back exist: "Back to listings" passes router state, and the browser Back
  // button doesn't — but a restored page above 1 means they were deep in the
  // list either way.
  const pendingReturnScrollRef = useRef(
    (location.state as { scrollToListings?: boolean } | null)?.scrollToListings === true ||
      readSavedListingsPage() > 1,
  );

  // Unlock quota state (for buyers with limited tiers)
  const [freeListingsAccess, setFreeListingsAccess] = useState<number | null>(null);
  const [freeUnlocksRemaining, setFreeUnlocksRemaining] = useState<number | null>(null);
  // Free (Basic) buyer: the same quota machinery, but the allowance GROWS by 2
  // each week of the month (unused ones carry over, reset on the 1st UTC), and
  // running out leads to /choose-plan instead of the $10 paid-unlock checkout.
  // freeListingsAccess is the amount earned so far this month.
  const [isBasicPlan, setIsBasicPlan] = useState(false);
  const [unlocksResetAt, setUnlocksResetAt] = useState<string | null>(null);
  // freeListingsAccess is null both before the quota loads and for unlimited
  // plans — this tells the two apart for the My Unlocks tab redirect.
  const [quotaLoaded, setQuotaLoaded] = useState(false);
  const [upgradePopup, setUpgradePopup] = useState<string | null>(null);
  const [unlockPopup, setUnlockPopup] = useState<string | null>(null);
  const [pendingNavigate, setPendingNavigate] = useState<string | null>(null);
  const [quotaFlash, setQuotaFlash] = useState(false);
  const [unlockedListingIds, setUnlockedListingIds] = useState<Set<string>>(new Set());
  const [lockedListingIds, setLockedListingIds] = useState<Set<string>>(new Set());
  const [ownLockedListingIds, setOwnLockedListingIds] = useState<Set<string>>(new Set());
  const [extraListingAmount, setExtraListingAmount] = useState<number | null>(null);
  // Paid-unlock checkout — opens as a popup over the marketplace instead of
  // routing away to /listings/:id/unlock (the buyer keeps their place in the grid).
  const [paidUnlockListing, setPaidUnlockListing] = useState<{ id: string; title: string | null } | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [togglingFavId, setTogglingFavId] = useState<string | null>(null);

  // "Make Offer" / Show Interest popup (buyers) — express interest directly from a listing card.
  const [interestedIds, setInterestedIds] = useState<Set<string>>(new Set());
  const [offerListing, setOfferListing] = useState<BusinessListing | null>(null);
  const [offerMessage, setOfferMessage] = useState("");
  const [offerSubmitting, setOfferSubmitting] = useState(false);

  // Applied filters come from the <ListingFilters /> component; sort stays here.
  // Seed from the same store <ListingFilters> persists to, so returning from a
  // listing detail restores the filtered view.
  const [appliedFilters, setAppliedFilters] = useState<ListingFilterValues>(() =>
    readStoredFilters(LISTINGS_FILTERS_KEY, EMPTY_FILTERS),
  );
  // Default to newest; buyers with completed preferences can still opt into
  // best_match. /recommended needs a live subscription, so free (Basic) buyers
  // don't get the option — picking it would only 402 — and it is part of the
  // AI Deal Matching module, so hiding that module (403 server-side) hides it too.
  const isModuleVisible = useModuleVisibility();
  // Super-admin switch for the Coming Soon pipeline (buyer / advisor column).
  // Off → no tab, no map toggle, and the combined total counts live listings only.
  const comingSoonOn = isModuleVisible("coming_soon_deals");
  // A ?tab=coming-soon deep link (or the map) must not open a switched-off tab.
  useEffect(() => {
    if (!comingSoonOn && marketTab === "coming_soon") setMarketTab("active");
  }, [comingSoonOn, marketTab]);
  const isBuyerWithPrefs = hasRole("buyer") && !!user?.buyer_preferences && !user?.needs_buyer_preferences
    && !isFreeBuyerUser(user) && isModuleVisible("ai_deal_matching");
  const [sortBy, setSortBy] = useState("newest");
  // Best Match ranks active listings (the applied filters narrow the set), so
  // it only counts as "on" while the option is actually offered.
  const bestMatch = sortBy === "best_match" && isBuyerWithPrefs && statusKpi === 3;

  // Only update applied filters when they actually change. The filters component
  // emits once on mount; ignoring that no-op keeps the restored page intact.
  const applyFilters = useCallback((next: ListingFilterValues) => {
    setAppliedFilters((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  }, []);

  const [categories, setCategories] = useState<LookupItem[]>([]);
  // Active listing count per industry — the category tiles' numbers.
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number> | null>(null);
  const [categoryImages, setCategoryImages] = useState<Record<string, string>>({});
  // A tile click is pushed into <ListingFilters> so it becomes a normal chip.
  const [categoryPreset, setCategoryPreset] = useState<{ ids: string[]; nonce: number } | undefined>(undefined);
  // TKT-00129: buyers asked to filter by advisor. This lookup is the advisors
  // who actually represent live listings, not the full staff roster.
  const [advisors, setAdvisors] = useState<{ id: string; name: string }[]>([]);
  const [countries, setCountries] = useState<LookupItem[]>([]);
  const [allStates, setAllStates] = useState<LookupItem[]>([]);
  const [listingSeenAt, setListingSeenAt] = useState<string | null>(null);

  // Trending listings
  const [trendingListings, setTrendingListings] = useState<TrendingListing[]>([]);

  // Pins for the hero map (all active listings, city-level coordinates)
  const [mapPoints, setMapPoints] = useState<ListingMapPoint[]>([]);
  const [mapPointsLoading, setMapPointsLoading] = useState(true);
  // Coming Soon pipeline deals as pins — powers the map's Active/Coming Soon toggle.
  const [comingSoonMapPoints, setComingSoonMapPoints] = useState<ListingMapPoint[]>([]);
  const [comingSoonMapLoading, setComingSoonMapLoading] = useState(true);

  // Advisor platform stats
  const [platformStats, setPlatformStats] = useState<{
    total_listings: number;
    listings_by_status: Record<string, number>;
    total_interests: number;
  } | null>(null);

  // Table row hover card
  const [hoveredListing, setHoveredListing] = useState<BusinessListing | null>(null);
  const [hoverCardPos, setHoverCardPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const hoverCardRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Triggered from the eye icon next to each table row's title (was row hover).
  const handleRowMouseEnter = (listing: BusinessListing, e: React.MouseEvent<HTMLElement>) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    const cardW = 340;
    const cardH = 380;
    const left = Math.max(8, (window.innerWidth - cardW) / 2);
    const top = Math.min(Math.max(rect.top - cardH / 2 + rect.height / 2, 8), window.innerHeight - cardH - 8);
    setHoverCardPos({ top, left });
    setHoveredListing(listing);
  };

  const handleRowMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setHoveredListing(null), 120);
  };

  const pageSize = viewMode === "grid" ? GRID_PAGE_SIZE : TABLE_PAGE_SIZE;

  useEffect(() => {
    // Cartogram data — fetched for every signed-in role (buyer, BL advisor,
    // admin). Fired before everything else so the map's pins aren't queued
    // behind the other startup calls on limited browser connections.
    if (user) {
      loadMapPoints("/business-listings/map-points", user.id, setMapPoints, setMapPointsLoading);
      loadMapPoints("/lead-pipeline/map-points", user.id, setComingSoonMapPoints, setComingSoonMapLoading);
    }

    api.get<LookupItem[]>("/lookups/categories").then(setCategories).catch(() => {});
    api.get<{ id: string; name: string }[]>("/lookups/advisors").then(setAdvisors).catch(() => {});
    api.get<LookupItem[]>("/lookups/countries").then(setCountries).catch(() => {});
    api.get<LookupItem[]>("/lookups/states").then(setAllStates).catch(() => {});
    api.get<TrendingListing[]>("/business-listings/trending?limit=12").then(setTrendingListings).catch(() => {});

    if (isAdminOrAgent) {
      api.get<{ count: number; last_seen_at: string | null }>("/business-listings/new-count")
        .then((data) => setListingSeenAt(data.last_seen_at))
        .catch(() => {});
      api.get<{ total_listings: number; listings_by_status: Record<string, number>; total_interests: number }>(
        "/business-listings/platform-stats"
      )
        .then(setPlatformStats)
        .catch(() => {});
    }

    if (user && !isOperator) {
      api
        .get<{
          is_basic: boolean;
          free_listings_access: number | null;
          free_unlocks_used: number;
          free_unlocks_remaining: number | null;
          free_unlocks_reset_at?: string | null;
          extra_listing_amount: number | null;
          unlocked_listing_ids: string[];
          locked_listing_ids: string[];
          own_locked_listing_ids: string[];
        }>("/tiers/my-unlocked-listings")
        .then((data) => {
          setIsBasicPlan(Boolean(data.is_basic));
          setFreeListingsAccess(data.free_listings_access);
          setFreeUnlocksRemaining(data.free_unlocks_remaining);
          setUnlocksResetAt(data.free_unlocks_reset_at ?? null);
          setQuotaLoaded(true);
          setExtraListingAmount(data.extra_listing_amount);
          setUnlockedListingIds(new Set(data.unlocked_listing_ids));
          setLockedListingIds(new Set(data.locked_listing_ids || []));
          setOwnLockedListingIds(new Set(data.own_locked_listing_ids || []));
        })
        .catch(() => setQuotaLoaded(true));
    } else {
      setQuotaLoaded(true);
    }

    if (user) {
      api.get<{ ids: string[] }>("/favorites/ids")
        .then((data) => setFavoriteIds(new Set(data.ids)))
        .catch(() => {});
    }

    if (user) {
      api.get<{ counts: Record<string, number>; active_revenue_total: number | null }>("/business-listings/status-counts")
        .then((data) => {
          setStatusCounts(data.counts);
          setActiveRevenueTotal(data.active_revenue_total ?? 0);
        })
        // Unfiltered browse total as a fallback, so the badge never sticks on "…".
        .catch(() =>
          api.get<{ total_elements: number }>("/business-listings/?listing_status_id=3&page=1&page_size=1")
            .then((data) => setStatusCounts((prev) => ({ ...prev, "3": data.total_elements ?? 0 })))
            .catch(() => {}),
        );
      // Seed the Coming Soon tab badge + combined total. Uses the count-only
      // endpoint (no subscription needed) so free/Basic buyers — who can't
      // browse the deals — still get the number instead of a stuck "…".
      api.get<{ counts: Record<string, number>; images?: Record<string, string> }>("/business-listings/category-counts")
        .then((data) => {
          setCategoryCounts(data.counts);
          setCategoryImages(data.images ?? {});
        })
        .catch(() => {});
      api.get<{ total_elements: number }>("/lead-pipeline/count")
        .then((data) => setComingSoonTotal(data.total_elements))
        .catch(() => setComingSoonTotal(0));
    }

    // Seed already-submitted interests so grid/table cards show "Interest sent"
    // across sessions, not just for offers made on this page load.
    if (user && hasRole("buyer")) {
      api.get<{ listing_ids: string[] }>("/interests/mine/listing-ids")
        .then((data) => setInterestedIds((prev) => new Set([...prev, ...data.listing_ids])))
        .catch(() => {});
    }
  }, [isAdminOrAgent, isOperator, user, location.key, hasRole]);

  const fetchListings = useCallback(async (targetPage = 1) => {
    if (!user) { setLoading(false); return; }
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
    } else {
      const el = marketplaceRef.current;
      if (el) {
        // Offset for the sticky top nav (h-16) plus a little breathing room.
        const y = el.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
    setLoading(true);
    setCurrentPage(targetPage);
    try {
      const pSize = viewMode === "grid" ? GRID_PAGE_SIZE : TABLE_PAGE_SIZE;
      let data: { total_elements: number; data: BusinessListing[] };
      if (marketTab === "unlocks") {
        // My Unlocks: every listing the buyer has unlocked, whatever its status
        // now — so no listing_status_id, and no Best Match ranking.
        const params = buildListingFilterParams(appliedFilters);
        params.set("page", String(targetPage));
        params.set("page_size", String(pSize));
        params.set("unlocked_only", "true");
        if (sortBy && sortBy !== "newest" && sortBy !== "best_match") params.set("order_by", sortBy);
        data = await api.get<{ total_elements: number; data: BusinessListing[] }>(`/business-listings/?${params}`);
      } else if (bestMatch) {
        // Personalised feed: the applied filters narrow the set, the BuyBox
        // score orders it (active listings only).
        const params = buildListingFilterParams(appliedFilters);
        params.set("page", String(targetPage));
        params.set("page_size", String(pSize));
        data = await api.get<{ total_elements: number; data: BusinessListing[] }>(`/business-listings/recommended?${params}`);
      } else {
        const params = buildListingFilterParams(appliedFilters);
        params.set("page", String(targetPage));
        params.set("page_size", String(pSize));
        if (sortBy && sortBy !== "newest" && sortBy !== "best_match") params.set("order_by", sortBy);
        // The status KPI bar drives which listing status the buyer is viewing.
        params.set("listing_status_id", String(statusKpi));
        data = await api.get<{ total_elements: number; data: BusinessListing[] }>(`/business-listings/?${params}`);
      }
      setListings(data.data);
      setTotal(data.total_elements);
    } catch {
      setListings([]);
      setTotal(0);
      toast.error("Failed to load listings. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  }, [user, appliedFilters, sortBy, bestMatch, toast, viewMode, statusKpi, marketTab]);

  // Re-fetch on browse changes. The first load (once the user is known)
  // restores the page saved for this session; afterwards, only a real
  // filters/sort/view change resets back to page 1. Keying off this signature
  // (rather than the fetchListings identity) keeps auth refreshes from
  // silently bouncing the restored page back to 1.
  // Only the Unlocks tab changes what this fetch loads (Coming Soon has its own).
  const unlocksTab = marketTab === "unlocks";
  const browseSig = JSON.stringify({ f: appliedFilters, s: sortBy, b: bestMatch, v: viewMode, st: statusKpi, u: unlocksTab });
  useEffect(() => {
    if (!user) return;
    if (!didInitialFetchRef.current) {
      didInitialFetchRef.current = true;
      lastBrowseSigRef.current = browseSig;
      fetchListings(initialPageRef.current);
      return;
    }
    if (lastBrowseSigRef.current !== browseSig) {
      lastBrowseSigRef.current = browseSig;
      // Filter/sort/view changes shouldn't yank the page around — only
      // pagination should auto-scroll back to the top of the list.
      skipScrollRef.current = true;
      fetchListings(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseSig, user]);

  // Follow in-app navigations that target a marketplace tab (?tab=coming-soon).
  // Deep links also scroll straight to the marketplace section so the target
  // tab is visible immediately instead of landing on the hero.
  useEffect(() => {
    const t = new URLSearchParams(location.search).get("tab");
    if (t === "coming-soon") setMarketTab("coming_soon");
    else if (t === "unlocks") setMarketTab("unlocks");
    else if (t === "active") setMarketTab("active");
    if (!t) return;
    const timer = setTimeout(() => {
      document.getElementById("marketplace")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => clearTimeout(timer);
  }, [location.search]);

  // Persist the current page so returning from a listing detail lands here.
  useEffect(() => {
    try {
      sessionStorage.setItem(LISTINGS_PAGE_KEY, String(currentPage));
    } catch {
      /* ignore storage errors */
    }
  }, [currentPage]);

  useEffect(() => {
    // Wait for the rows themselves: `loading` starts false, and the marketplace
    // section renders before any data, so scrolling to it too early lands on a
    // near-empty container that then grows under the viewport.
    if (!pendingReturnScrollRef.current || loading || listings.length === 0) return;
    pendingReturnScrollRef.current = false;
    const el = marketplaceRef.current;
    if (!el) return;
    // Offset for the sticky top nav (h-16), matching fetchListings' own scroll.
    const y = el.getBoundingClientRect().top + window.scrollY - 72;
    // Instant: smooth-scrolling the length of the hero on entry looks like a
    // glitch, and this is a position restore rather than a user gesture.
    window.scrollTo({ top: Math.max(0, y), behavior: "auto" });
  }, [loading, listings]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Unfiltered Active count for the tab badge + "Total N listings". Never falls
  // back to `total` — that's the filtered page total and moved with every filter.
  const activeCount: number | null = statusCounts["3"] ?? null;

  // My Unlocks only means something on a metered plan (Basic, Explorer). An
  // unlimited plan (Operator: free_listings_access null) opens listings without
  // recording an unlock, so the tab would always be empty — hide it. Note
  // `isOperator` above is the STAFF flag, not the Operator plan.
  const showUnlocksTab = isBuyerView && !isOperator && freeListingsAccess !== null;
  useEffect(() => {
    if (quotaLoaded && !showUnlocksTab && marketTab === "unlocks") setMarketTab("active");
  }, [quotaLoaded, showUnlocksTab, marketTab]);

  const needsUnlock = (listingId: string): boolean => {
    if (isOperator || !user) return false;
    if (freeListingsAccess === null) return false;
    return !unlockedListingIds.has(listingId);
  };

  // Shared: call the unlock API and navigate. Used by the direct path (plenty
  // of quota left) and by the popup OK button after user acknowledges the warning.
  const doUnlock = async (listingId: string) => {
    try {
      const result = await api.post<{ success: boolean; free_remaining: number | null }>(
        `/tiers/free-unlock/${listingId}`,
        {}
      );
      if (result.success) {
        setUnlockedListingIds((prev) => new Set([...prev, listingId]));
        if (result.free_remaining !== null) {
          setFreeUnlocksRemaining(result.free_remaining);
        }
        navigate(`/listings/${listingId}`);
      }
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(err.message || "This listing is temporarily locked by another user.");
      } else if (err instanceof ApiError && err.status === 402) {
        // Quota is exhausted. Basic buyers have nothing to buy per listing —
        // their next deal is an upgrade — while subscribers get the paid unlock.
        if (isBasicPlan) {
          setUpgradePopup(err.message);
        } else {
          const listing = listings.find((l) => l.id === listingId);
          setPaidUnlockListing({ id: listingId, title: listing?.title ?? null });
        }
      } else {
        toast.error(err?.message || "Unable to open listing. Please try again.");
      }
    }
  };

  const handleViewDetails = (listingId: string) => {
    // Free (Basic) buyers are NOT bounced to /choose-plan any more: they hold a
    // weekly allowance of full-detail unlocks and only see the upgrade prompt
    // once it runs out (below, and on the 402 from doUnlock). Subscribers who
    // are out of unlocks go straight to the paid-unlock checkout — this path is
    // also reached from map pins, which have no quotaExhausted button of their own.
    if (!isOperator && freeListingsAccess !== null && freeUnlocksRemaining !== null
        && freeUnlocksRemaining <= 0 && !unlockedListingIds.has(listingId)) {
      const listing = listings.find((l) => l.id === listingId);
      handleQuotaExhausted({ id: listingId, title: listing?.title ?? null });
      return;
    }

    const lockedByAnother = lockedListingIds.has(listingId) && !ownLockedListingIds.has(listingId);
    if (!isOperator && lockedByAnother && !unlockedListingIds.has(listingId)) {
      toast.error("This listing is temporarily locked by another user.");
      return;
    }

    if (isOperator || freeListingsAccess === null) {
      navigate(`/listings/${listingId}`);
      return;
    }
    if (unlockedListingIds.has(listingId)) {
      navigate(`/listings/${listingId}`);
      return;
    }

    // Show warning popup IMMEDIATELY if this unlock will bring remaining to ≤3
    const projectedRemaining = freeUnlocksRemaining !== null ? freeUnlocksRemaining - 1 : null;
    if (projectedRemaining !== null && projectedRemaining <= 3) {
      const period = isBasicPlan ? "earned so far this month on the free Basic plan" : "this month";
      const msg = projectedRemaining <= 0
        ? `You're about to use your last free unlock (${freeListingsAccess} total ${period}). ${isBasicPlan ? "2 more arrive next week, or upgrade" : "Upgrade"} your plan to unlock more listings.`
        : `Only ${projectedRemaining} free unlock${projectedRemaining === 1 ? "" : "s"} will remain after this (out of ${freeListingsAccess} ${period}).`;
      setPendingNavigate(listingId);
      setUnlockPopup(msg);
      setQuotaFlash(true);
      setTimeout(() => setQuotaFlash(false), 1200);
      return; // API call + navigation happen when user clicks OK
    }

    // Plenty of quota left — call API and navigate directly
    doUnlock(listingId);
  };

  const handleToggleFavorite = async (listingId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user || hasRole("admin")) return;
    // Favorites are members-only — send free (Basic) buyers to plan selection
    // instead of letting the API reject the call with a 402.
    if (isFreeBuyerUser(user)) {
      navigate("/choose-plan");
      return;
    }
    setTogglingFavId(listingId);
    try {
      // Explicit add/remove (not a server-side toggle) so a stale heart can
      // never flip the opposite way of what the buyer clicked.
      const res = favoriteIds.has(listingId)
        ? await api.delete<{ favorited: boolean }>(`/favorites/${listingId}`)
        : await api.put<{ favorited: boolean }>(`/favorites/${listingId}`, {});
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        res.favorited ? next.add(listingId) : next.delete(listingId);
        return next;
      });
    } catch {
      toast.error("Failed to update favorites");
    } finally {
      setTogglingFavId(null);
    }
  };

  const openOffer = (listing: BusinessListing, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) { navigate("/sign-in"); return; }
    // Making an offer is members-only, except on a deal a free (Basic) buyer has
    // unlocked — the unlock opens the advisor chat (and the NDA flow) for that
    // deal. Anything else goes to plan selection rather than a form /interests would 402.
    if (isFreeBuyerUser(user) && !unlockedListingIds.has(listing.id)) {
      navigate("/choose-plan");
      return;
    }
    setOfferMessage("");
    setOfferListing(listing);
  };

  const submitOffer = async () => {
    if (!offerListing) return;
    setOfferSubmitting(true);
    try {
      const res = await api.post<{ acknowledgment?: string | null }>("/interests/", { listing_id: offerListing.id, message: offerMessage.trim() || undefined });
      setInterestedIds((prev) => new Set(prev).add(offerListing.id));
      // Niva's auto-acknowledgment; the same text opens the interest thread.
      toast.success(res.acknowledgment ? `Niva: ${res.acknowledgment}` : "Interest submitted! The advisor will be in touch.");
      setOfferListing(null);
      setOfferMessage("");
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        // Already interested (e.g. from another tab, or the seed request
        // failed) — reflect that on the card instead of leaving "Make offer".
        setInterestedIds((prev) => new Set(prev).add(offerListing.id));
        setOfferListing(null);
        setOfferMessage("");
        toast.success("You've already expressed interest in this listing.");
      } else {
        toast.error(err instanceof Error ? err.message : "Failed to submit interest");
      }
    } finally {
      setOfferSubmitting(false);
    }
  };

  // ID-based helpers (used for filter chips where we only have the ID)
  const stateNameById = (id: number | null) =>
    id ? (allStates.find((s) => s.id === id)?.name || null) : null;

  const countryNameById = (id: number | null) =>
    id ? (countries.find((c) => c.id === id)?.name || null) : null;

  // Listing-level location label — uses backend-resolved names first, then falls
  // back to local lookup. "United States" is omitted (it's the default market and
  // just adds noise); any other country still shows.
  const listingLocation = (l: { city_name?: string | null; state_name?: string | null; country_name?: string | null; state_id?: number | null; country_id?: number | null }) => {
    const country = l.country_name || countryNameById(l.country_id ?? null);
    return [
      l.city_name,
      l.state_name || stateNameById(l.state_id ?? null),
      country && country !== "United States" ? country : null,
    ].filter(Boolean).join(", ");
  };

  const categoryName = (id: number | null) => {
    if (!id) return "Business listing";
    return categories.find((c) => c.id === id)?.name || "Business listing";
  };

  const formatPrice = (price: number | null) => {
    if (!price) return "N/A";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(price);
  };

  /** Quota spent: Basic buyers see the upgrade prompt (there is nothing to buy
   *  per deal on Basic), subscribers get the paid-unlock checkout. */
  const handleQuotaExhausted = (listing: { id: string; title: string | null }) => {
    if (isBasicPlan) {
      setUpgradePopup(
        `You've used all your free unlocks for now.${unlocksResetAt ? ` 2 more arrive ${formatResetDay(unlocksResetAt)}.` : ""} Upgrade your plan to keep unlocking deals.`
      );
      return;
    }
    setPaidUnlockListing({ id: listing.id, title: listing.title });
  };

  const listingAction = (listing: BusinessListing) => {
    const locked = needsUnlock(listing.id);
    const lockedByAnother = lockedListingIds.has(listing.id) && !ownLockedListingIds.has(listing.id);
    const quotaExhausted = locked && freeUnlocksRemaining !== null && freeUnlocksRemaining <= 0;
    const lockBlocksUnlock = lockedByAnother && !isOperator && !unlockedListingIds.has(listing.id);
    return { locked, lockedByAnother, quotaExhausted, lockBlocksUnlock };
  };

  // Signed-out visitors at "/" get the marketing landing page instead of the
  // bare hero — everything below it is gated behind `user` anyway. (Logged-in
  // users never reach "/": BrowseGuard forwards them to their role home.)
  if (!user) {
    return (
      <>
        <AnnouncementBanner />
        <PublicLanding categories={categories} />
      </>
    );
  }

  // Feature banners that slide after the hero (shared with the admin listings page).
  const heroBanners = featureBanners(user.org_slug, hasRole("buyer") || hasRole("admin"));

  return (
    <>
    <AnnouncementBanner />
    <div className="public-stage pb-8">
      {/* Hero — pinned to the Dark · Gold look in BOTH themes: the data-theme
          attribute re-scopes every palette variable and [data-theme="dark"]
          rule (hero shell, pills, text-gradient) to their dark values for this
          box only, so the light theme renders it identically to dark mode.
          The inline transparent background undoes the [data-theme="dark"]
          rule's own background-color so only the rounded shell paints dark,
          not the full-width section behind it. */}
      <section data-theme="dark" style={{ backgroundColor: "transparent" }} className="px-4 pt-6 sm:px-6 lg:px-8">
        <div className={shellWidth}>
        <HeroCarousel
          banners={heroBanners}
          hero={
        <div className="public-hero-shell hero-niva h-full w-full rounded-[1.85rem] shadow-glow">
          <div className="public-grid-lattice absolute inset-0 opacity-45" />
          {/* Niva, cut from the feature banners so slide 1 matches them. The
              left edge fades into the shell; hidden below lg, where the text
              column needs the full width. */}
          <img
            src="/images/home-banners/niva-hero.webp"
            alt="Niva, your AI deal assistant"
            className="pointer-events-none absolute inset-y-0 right-0 hidden h-full w-[42%] select-none object-cover lg:block"
            style={{
              objectPosition: "40% 15%",
              maskImage: "linear-gradient(to right, transparent 0%, #000 24%)",
              WebkitMaskImage: "linear-gradient(to right, transparent 0%, #000 24%)",
            }}
            draggable={false}
          />
          <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[42%] lg:block">
            <span className="absolute bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap inline-flex items-center gap-2 rounded-full border border-primary/50 bg-black/60 px-4 py-1.5 text-sm text-foreground backdrop-blur-sm">
              <span className="font-semibold text-primary">Niva</span>
              <span className="text-primary/60">&bull;</span>
              Your AI Deal Assistant
            </span>
          </div>
          {/* TKT-00130: stay a single stacked column on phones — never squeeze
              the headline beside another shrink-0 box. */}
          <div className="relative z-10 flex h-full flex-col justify-center px-6 py-7 sm:px-10 sm:py-8 lg:w-[60%]">
            <div className="inline-flex w-fit items-center gap-2.5 rounded-full border border-primary/50 bg-black/20 px-4 py-1.5 text-[0.7rem] font-bold uppercase tracking-[0.16em] text-primary sm:text-xs">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Business Marketplace
              <span className="text-primary/60">•</span>
              Off-Market Deals
            </div>

            <h1 className="heading-bold mt-4 font-display text-3xl leading-[1.04] text-foreground sm:text-4xl xl:text-[2.75rem]">
              Exclusive Access to Premium Off-Market Business Listings.
              <span className="text-gradient block pb-1">Members Only.</span>
            </h1>

            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground/85 sm:text-base">
              Browse active listings, compare location and valuation quickly, and move from discovery to action in a polished experience built for real operators.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {isOperator ? (
                <Link to="/listings/new">
                  <Button size="lg" className="h-11 rounded-xl px-5 shadow-glow">
                    Create listing
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              ) : (
                <Link to="/profile">
                  <Button size="lg" className="h-11 rounded-xl px-5 shadow-glow">
                    View profile
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              )}
              <a href="#marketplace">
                <Button size="lg" variant="outline" className="h-11 rounded-xl border-primary/45 bg-black/20 px-5 text-foreground hover:bg-black/35">
                  Browse active listings
                </Button>
              </a>
              {/* Feature chips share the CTA row (like the banners) to keep the slide short. */}
              {[
                { icon: TrendingUp, label: "Live Market Flow" },
                { icon: Users, label: "Buyer-Seller Match" },
                { icon: Building2, label: "Verified Inventory" },
              ].map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-black/20 px-3 py-1.5 text-xs text-foreground sm:text-sm">
                  <Icon className="h-4 w-4 text-primary" />
                  {label}
                </span>
              ))}
            </div>

            {/* Total revenue portfolio across live active listings */}
            {activeRevenueTotal !== null && activeRevenueTotal > 0 && (
              <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-primary/20 pt-3">
                <span className="text-2xl font-extrabold leading-none text-primary tabular-nums">
                  {fmtMillions(activeRevenueTotal)}
                </span>
                <span className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  Active revenue portfolio
                </span>
                <span className="text-sm text-muted-foreground">
                  across{" "}
                  <span className="font-bold text-primary tabular-nums">
                    {(statusCounts["3"] ?? 0).toLocaleString()}
                  </span>{" "}
                  active listings
                </span>
              </div>
            )}
          </div>
        </div>
          }
        />
        </div>
      </section>

      {/* Trending Listings — below the hero for every signed-in role */}
      {user && <TrendingSection items={trendingListings} />}

      {/* Market explorer — US state cartogram + markets panel (buyer, BL advisor, admin) */}
      {user && (
        <section className="px-4 pt-6 sm:px-6 lg:px-8">
          <div className={shellWidth}>
            <MarketExplorer
              points={mapPoints}
              loading={mapPointsLoading}
              onView={handleViewDetails}
              comingSoonPoints={comingSoonOn ? comingSoonMapPoints : undefined}
              comingSoonLoading={comingSoonMapLoading}
              onViewComingSoon={() => {
                // Coming-soon deals live in the marketplace's Coming Soon tab —
                // switch to it and bring the marketplace into view.
                setMarketTab("coming_soon");
                marketplaceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            />
          </div>
        </section>
      )}

      {/* Advisor / Admin platform dashboard */}
      {isAdminOrAgent && platformStats && (
        <section className="px-4 pt-6 sm:px-6 lg:px-8">
          <div className={shellWidth}>
            <div className="mb-3 flex items-center gap-2">
              <span className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary/70">Platform Overview</span>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-2xl border border-border/60 bg-white/80 p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Total Listings</span>
                  <Building2 className="h-4 w-4 text-primary/50" />
                </div>
                <div className="mt-2 text-3xl font-black tracking-tight text-foreground">{platformStats.total_listings}</div>
              </div>
              <div className="rounded-2xl border border-border/60 bg-white/80 p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Active</span>
                  <TrendingUp className="h-4 w-4 text-emerald-500/70" />
                </div>
                <div className="mt-2 text-3xl font-black tracking-tight text-foreground">{platformStats.listings_by_status["active"] ?? 0}</div>
              </div>
              <div className="rounded-2xl border border-border/60 bg-white/80 p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Under Review</span>
                  <Search className="h-4 w-4 text-amber-500/70" />
                </div>
                <div className="mt-2 text-3xl font-black tracking-tight text-foreground">{platformStats.listings_by_status["under_review"] ?? 0}</div>
              </div>
              <div className="rounded-2xl border border-border/60 bg-white/80 p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Buyer Interests</span>
                  <Heart className="h-4 w-4 text-rose-500/70" />
                </div>
                <div className="mt-2 text-3xl font-black tracking-tight text-foreground">{platformStats.total_interests}</div>
              </div>
            </div>
            {Object.keys(platformStats.listings_by_status).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.entries(platformStats.listings_by_status).map(([status, count]) => (
                  <span
                    key={status}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border/50 bg-white/70 px-3 py-1 text-xs font-semibold text-muted-foreground capitalize"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/40" />
                    {status.replace(/_/g, " ")}: {count}
                  </span>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Status KPI bar — buyer marketplace only: click a card to filter listings by status */}
      {/* Hidden for now — temporarily disabled for buyers (set `false` -> `true` to restore) */}
      {false && isBuyerView && (
        <div className="px-4 pt-6 sm:px-6 lg:px-8">
          <div className={shellWidth}>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 lg:grid-cols-5">
              {BUYER_STATUS_KPIS.map((kpi) => {
                const Icon = kpi.icon;
                const selected = statusKpi === kpi.id;
                const count = statusCounts[String(kpi.id)] ?? 0;
                return (
                  <button
                    key={kpi.id}
                    type="button"
                    onClick={() => setStatusKpi(kpi.id)}
                    aria-pressed={selected}
                    className={cn(
                      "group relative flex items-center gap-3 overflow-hidden rounded-2xl border bg-card px-4 py-3.5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                      selected
                        ? cn("border-transparent shadow-md ring-2", kpi.selBg, kpi.selRing)
                        : "border-border/60 hover:border-border"
                    )}
                  >
                    {/* Left accent bar — solid when selected, hinted on hover */}
                    <span
                      className={cn(
                        "absolute inset-y-0 left-0 w-1 transition-opacity duration-200",
                        kpi.bar,
                        selected ? "opacity-100" : "opacity-0 group-hover:opacity-50"
                      )}
                    />
                    <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl shadow-sm", kpi.chip)}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className={cn("block text-2xl font-extrabold leading-none tracking-tight tabular-nums", selected ? kpi.num : "text-foreground")}>
                        {fmtNum(count)}
                      </span>
                      <span className="mt-1.5 block truncate text-[0.66rem] font-semibold uppercase tracking-[0.13em] text-muted-foreground">
                        {kpi.label}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Filters — quick bar + drawer (reusable component) */}
      {user && (
        <ListingFilters
          onApply={applyFilters}
          fullWidth={isBuyerView || hasRole("bl_advisor", "admin")}
          storageKey={LISTINGS_FILTERS_KEY}
          categoryPreset={categoryPreset}
          // Saved filters also remember the sort, status KPI and tab. A tab or
          // sort the viewer can no longer use is reset by the guards above.
          view={{ sortBy, statusKpi, marketTab }}
          onRestoreView={(v) => {
            if (v.sortBy && (v.sortBy !== "best_match" || isBuyerWithPrefs)) setSortBy(v.sortBy);
            // Filters saved before view state existed open on Available listings.
            setStatusKpi(v.statusKpi ?? 3);
            setMarketTab(v.marketTab ?? "active");
            // Clicking a saved filter in the sidebar lands on the results, not
            // wherever the page happened to be scrolled.
            requestAnimationFrame(() => {
              const el = marketplaceRef.current;
              if (!el) return;
              const y = el.getBoundingClientRect().top + window.scrollY - 72;
              window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
            });
          }}
          // Omitted entirely while the lookup is empty (still loading, or no
          // advisor has a listing yet) so the select never renders with nothing
          // but "All advisors" in it.
          advisorOptions={
            advisors.length > 0
              ? advisors.map((a) => ({ value: a.id, label: a.name }))
              : undefined
          }
        />
      )}

      {/* Listings — buyers, BL advisors, and admins all browse here */}
      {user && (
        <section id="marketplace" ref={marketplaceRef} className="px-4 py-8 sm:px-6 lg:px-8">
          <div className={shellWidth}>
            {/* Category tiles sit above the listings header */}
            <CategoryTiles
              categories={categories}
              counts={marketTab === "active" ? categoryCounts : null}
              images={categoryImages}
              selectedIds={appliedFilters.categoryIds}
              onSelect={(id) => setCategoryPreset({ ids: id === null ? [] : [String(id)], nonce: Date.now() })}
            />

            {/* Header row: title | quota banner | view toggle — all horizontal */}
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                  {statusKpi === 3
                    ? "Available listings"
                    : `${BUYER_STATUS_KPIS.find((k) => k.id === statusKpi)?.label ?? ""} listings`}
                </h2>
                {/* Combined total — sits between the heading and the tabs */}
                <p className="mt-1 text-sm text-muted-foreground">
                  Total{" "}
                  <span className="font-bold tabular-nums text-primary">
                    {activeCount === null || (comingSoonOn && comingSoonTotal === null)
                      ? "…"
                      : (activeCount + (comingSoonOn ? comingSoonTotal ?? 0 : 0)).toLocaleString()}
                  </span>{" "}
                  listings
                </p>
                {/* Active / Coming Soon tabs — totals are exact numbers */}
                <div className="mt-3 inline-flex rounded-xl border border-border/70 bg-muted p-1">
                  <button
                    type="button"
                    onClick={() => setMarketTab("active")}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors",
                      marketTab === "active" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <CircleDot className="h-4 w-4" /> Active
                    <span
                      className={cn(
                        "rounded-full px-1.5 py-0.5 text-[0.68rem] font-bold tabular-nums",
                        marketTab === "active" ? "bg-primary-foreground/15" : "bg-primary/15 text-primary",
                      )}
                    >
                      {activeCount === null ? "…" : activeCount.toLocaleString()}
                    </span>
                  </button>
                  {comingSoonOn && (
                  <button
                    type="button"
                    onClick={() => setMarketTab("coming_soon")}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors",
                      marketTab === "coming_soon" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <Sparkles className="h-4 w-4" /> Coming Soon
                    <span
                      className={cn(
                        "rounded-full px-1.5 py-0.5 text-[0.68rem] font-bold tabular-nums",
                        marketTab === "coming_soon" ? "bg-primary-foreground/15" : "bg-primary/15 text-primary",
                      )}
                    >
                      {comingSoonTotal === null ? "…" : comingSoonTotal.toLocaleString()}
                    </span>
                  </button>
                  )}
                  {showUnlocksTab && (
                  <button
                    type="button"
                    onClick={() => setMarketTab("unlocks")}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors",
                      marketTab === "unlocks" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <KeyRound className="h-4 w-4" /> My Unlocks
                    <span
                      className={cn(
                        "rounded-full px-1.5 py-0.5 text-[0.68rem] font-bold tabular-nums",
                        marketTab === "unlocks" ? "bg-primary-foreground/15" : "bg-primary/15 text-primary",
                      )}
                    >
                      {unlockedListingIds.size.toLocaleString()}
                    </span>
                  </button>
                  )}
                </div>
              </div>

              {/* Unlock quota banner — inline pill, limited-tier buyers only */}
              {marketTab !== "coming_soon" && freeListingsAccess !== null && freeListingsAccess > 0 && !isOperator && freeUnlocksRemaining !== null && (
                <div
                  className={cn(
                    "flex items-center gap-2.5 rounded-full border px-4 py-2 transition-all duration-300",
                    freeUnlocksRemaining <= 0
                      ? "border-rose-500/30 bg-rose-500/10 text-rose-500"
                      : freeUnlocksRemaining <= Math.ceil(freeListingsAccess * 0.25)
                        ? "border-amber-500/30 bg-amber-500/10 text-amber-500"
                        : "border-primary/25 bg-primary/5 text-primary",
                    quotaFlash && "scale-105 shadow-md"
                  )}
                >
                  <Zap className={cn("h-3.5 w-3.5 shrink-0", quotaFlash && "animate-bounce")} />
                  <span className="text-sm font-semibold whitespace-nowrap">
                    {/* Basic earns 2 a week that carry over to month end, so it says
                        so — a plain "unlocks left" reads as the subscribers' refill. */}
                    {freeUnlocksRemaining <= 0
                      ? isBasicPlan
                        ? `No free unlocks left${unlocksResetAt ? ` · 2 more ${formatResetDay(unlocksResetAt)}` : ""}`
                        : "No unlocks remaining"
                      : `${freeUnlocksRemaining} / ${freeListingsAccess} ${isBasicPlan ? "free unlocks left this month" : "unlocks left"}`}
                  </span>
                  <div className="h-1.5 w-20 rounded-full bg-black/10 overflow-hidden">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all duration-700",
                        freeUnlocksRemaining <= 0 ? "bg-rose-400" : freeUnlocksRemaining <= Math.ceil(freeListingsAccess * 0.25) ? "bg-amber-400" : "bg-primary"
                      )}
                      style={{ width: `${Math.min(100, Math.max(0, Math.round((freeUnlocksRemaining / freeListingsAccess) * 100)))}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                {/* Stays mounted while loading so the header doesn't jump on every
                    sort/filter change. */}
                {marketTab !== "coming_soon" && (
                <div className="flex items-center gap-2">
                  <Label htmlFor="sortBy" className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground whitespace-nowrap">Sort by</Label>
                  <select
                    id="sortBy"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="h-9 rounded-lg border border-border/70 bg-card px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
                  >
                    {isBuyerWithPrefs && marketTab === "active" && <option value="best_match">✦ Best Match</option>}
                    <option value="newest">Newest</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                    <option value="revenue_asc">Revenue: Low to High</option>
                    <option value="revenue_desc">Revenue: High to Low</option>
                    <option value="ebitda_asc">EBITDA: Low to High</option>
                    <option value="ebitda_desc">EBITDA: High to Low</option>
                  </select>
                </div>
                )}
                <div className="flex items-center gap-1 rounded-xl border border-border/80 bg-card p-1">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      viewMode === "grid"
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    )}
                    title="Grid view"
                  >
                    <LayoutGrid className="h-4 w-4" />
                    <span className="hidden sm:inline">Grid</span>
                  </button>
                  <button
                    onClick={() => setViewMode("table")}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      viewMode === "table"
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    )}
                    title="Table view"
                  >
                    <List className="h-4 w-4" />
                    <span className="hidden sm:inline">Table</span>
                  </button>
                </div>
              </div>
            </div>

            {marketTab === "coming_soon" ? (
              /* ── Coming Soon tab — deal pipeline browser (same filters + view toggle) ── */
              <ComingSoonDeals
                filters={appliedFilters}
                viewMode={viewMode}
                pageSize={pageSize}
                onTotalChange={setComingSoonTotal}
              />
            ) : loading ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <div key={item} className="rounded-xl border border-border/80 bg-card p-5 shadow-card">
                    <div className="h-5 w-3/4 skeleton" />
                    <div className="mt-3 h-4 w-full skeleton" />
                    <div className="mt-2 h-4 w-2/3 skeleton" />
                    <div className="mt-6 h-10 w-full skeleton" />
                  </div>
                ))}
              </div>
            ) : listings.length === 0 && marketTab === "unlocks" ? (
              <div className="rounded-2xl border border-dashed border-border/80 bg-card px-6 py-12 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-muted/60 text-muted-foreground/60">
                  <KeyRound className="h-7 w-7" />
                </div>
                <h3 className="mt-4 text-xl font-bold text-foreground">No unlocked deals yet</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {isBasicPlan
                    ? `Open any listing to unlock it — you get ${freeListingsAccess ?? 2} free unlocks every week, and each one lets you message the BL Advisor.`
                    : "Deals you unlock show up here."}
                </p>
                <Button className="mt-5" onClick={() => setMarketTab("active")}>
                  Browse active listings
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            ) : listings.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border/80 bg-card px-6 py-12 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-muted/60 text-muted-foreground/60">
                  <Building2 className="h-7 w-7" />
                </div>
                <h3 className="mt-4 text-xl font-bold text-foreground">No listings found</h3>
                <p className="mt-2 text-sm text-muted-foreground">Try broadening your filters or changing the price range.</p>
              </div>
            ) : viewMode === "grid" ? (
              /* ── Grid view ── */
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {listings.map((listing) => {
                  const locationLabel = listingLocation(listing);
                  const showNewBadge = Boolean(
                    isAdminOrAgent && listing.created_at && (!listingSeenAt || new Date(listing.created_at) > new Date(listingSeenAt))
                  );
                  const { lockBlocksUnlock, quotaExhausted } = listingAction(listing);
                  const isSeen = !isOperator && unlockedListingIds.has(listing.id);

                  const cf = listing.asking_price && listing.ebitda && listing.ebitda !== 0
                    ? (listing.asking_price / listing.ebitda).toFixed(2) + "X"
                    : null;
                  const imgSrc = listing.listing_image_url ? assetUrl(listing.listing_image_url) : null;
                  const advisorInitials = listing.agent_name
                    ? listing.agent_name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()
                    : "";
                  const showMakeOffer = isBuyerView && listing.listing_status_id === 3;

                  return (
                    <div key={listing.id}
                      style={isBuyerView ? { borderColor: "rgba(201,161,74,0.4)", borderWidth: "1px" } : undefined}
                      className={cn(
                        "hover-lift public-listing-card group flex flex-col overflow-hidden rounded-2xl border border-border/70 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
                        isSeen && "opacity-95"
                      )}>

                      {/* Cover image */}
                      <div className="relative h-44 w-full shrink-0 overflow-hidden">
                        {imgSrc ? (
                          <>
                            <img src={imgSrc} alt={listing.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/25" />
                          </>
                        ) : (
                          <div className="flex h-full w-full items-end bg-[linear-gradient(135deg,hsl(40_20%_14%),hsl(40_16%_9%))]">
                            <div
                              className="pointer-events-none absolute inset-0 opacity-40"
                              style={{ backgroundImage: "repeating-linear-gradient(135deg, hsl(43 30% 60% / 0.08) 0 10px, transparent 10px 20px)" }}
                            />
                            <span className="relative m-3 text-xs text-white/60">img · {categoryName(listing.industry_category_id).toLowerCase()}</span>
                          </div>
                        )}
                        {/* Category badge */}
                        <div className="absolute left-3 top-3">
                          {/* Sits on dark glass over the photo in BOTH themes, so the
                              label is white, not foreground (dark ink in light mode). */}
                          <span className="inline-flex items-center rounded-md border border-primary/25 bg-black/60 px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.12em] text-white/90 backdrop-blur-sm">
                            {categoryName(listing.industry_category_id)}
                          </span>
                        </div>
                        {/* Favorite heart button */}
                        {user && !hasRole("admin") && (
                          <button
                            onClick={(e) => handleToggleFavorite(listing.id, e)}
                            disabled={togglingFavId === listing.id}
                            title={favoriteIds.has(listing.id) ? "Remove from favorites" : "Add to favorites"}
                            className="absolute left-3 bottom-3 flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-black/50 backdrop-blur-sm transition-colors hover:bg-black/70 disabled:opacity-50"
                          >
                            <Heart className={cn("h-4 w-4 transition-colors", favoriteIds.has(listing.id) ? "fill-rose-500 text-rose-500" : "text-white/70")} />
                          </button>
                        )}
                        {/* Match score badge (best_match sort only) */}
                        {sortBy === "best_match" && listing.match_score !== undefined && listing.match_score > 0 && (
                          <div className="absolute bottom-3 right-3">
                            <span
                              title={listing.match_reasons?.join(" · ") ?? "Match score"}
                              className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-black/60 px-2.5 py-1 text-[0.62rem] font-bold text-primary shadow-sm backdrop-blur-sm"
                            >
                              <Zap className="h-3 w-3" />
                              {Math.round(listing.match_score)}% match
                            </span>
                          </div>
                        )}
                        {/* Lock / Hot / New badges */}
                        <div className="absolute right-3 top-3 flex flex-col items-end gap-1">
                          {lockBlocksUnlock && (
                            <span title="Locked by another user"
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-amber-400/40 bg-black/50 text-amber-300 backdrop-blur-sm">
                              <Lock className="h-4 w-4" />
                            </span>
                          )}
                          <ListingHotBadge published_at={listing.published_at} size="md" />
                          {!getListingHotBadge(listing.published_at) && showNewBadge && (
                            <span className="badge-new-flash rounded-full border border-primary/40 bg-primary/20 px-2.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.12em] text-primary backdrop-blur-sm">
                              New
                            </span>
                          )}
                          {isSeen && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-black/50 px-2 py-0.5 text-[0.62rem] font-semibold text-white/80 backdrop-blur-sm">
                              <Eye className="h-3 w-3" />
                              Viewed
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Body */}
                      <div className="flex flex-1 flex-col gap-3 p-4">
                        <div>
                          {/* Reference code above the title. The row keeps its
                              height even when a listing has no code, so the
                              titles below still line up across cards. */}
                          <div className="mb-1 flex h-4 items-center">
                            {listing.reference_code && (
                              <span className="font-mono text-xs font-bold tracking-wider text-primary">
                                {listing.reference_code}
                              </span>
                            )}
                          </div>
                          {/* Two-line clamp with a reserved two-line height: long
                              titles ellipsize, short ones don't shrink the block,
                              so every card's KPI grid starts at the same y. */}
                          {/* The reserved height sits on the wrapper, never on the
                              clamped h3 itself — a -webkit-box forced taller than
                              its 2 clamped lines leaks the third line into view. */}
                          <div className="min-h-[4.15rem]">
                            <h3 className="line-clamp-2 font-display text-xl font-bold leading-snug text-foreground">
                              {user ? listing.title : categoryName(listing.industry_category_id)}
                            </h3>
                          </div>
                        </div>

                        {/* Financial metrics — Asking Price is the headline figure
                            on its own full-width row; Revenue / EBITDA / CF Mult
                            share one row beneath it. Always all four boxes so every
                            card lines up; missing figures render an em dash. */}
                        <div className="overflow-hidden rounded-xl border border-border/60 bg-primary/[0.06]">
                          <div className="px-4 pb-3 pt-3.5">
                            <div className="text-[0.62rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Asking Price</div>
                            <div className={cn(
                              "mt-1 truncate text-[1.9rem] font-extrabold leading-none tabular-nums",
                              listing.asking_price ? "text-primary" : "text-muted-foreground/40",
                            )}>
                              {listing.asking_price ? fmtCompact(listing.asking_price) : "—"}
                            </div>
                          </div>
                          <div className="grid grid-cols-3 divide-x divide-border/60 border-t border-border/60 bg-card/60">
                            {([
                              ["Revenue", listing.revenue ? fmtCompact(listing.revenue) : null],
                              ["EBITDA", listing.ebitda ? fmtCompact(listing.ebitda) : null],
                              ["CF Mult", cf || null],
                            ] as [string, string | null][]).map(([label, value]) => (
                              <div key={label} className="min-w-0 px-4 py-2.5">
                                <div className="text-[0.58rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">{label}</div>
                                <div className={cn(
                                  "mt-1 truncate text-[0.95rem] font-bold tabular-nums",
                                  value ? "text-foreground" : "text-muted-foreground/40",
                                )}>
                                  {value ?? "—"}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Location + BL Advisor — mt-auto pins this row (and the
                            buttons after it) to the card bottom, so cards with
                            fewer KPI tiles stay aligned with their neighbors. */}
                        <div className="mt-auto flex items-center justify-between gap-2 text-xs text-muted-foreground">
                          {locationLabel ? (
                            <span className="flex min-w-0 items-center gap-1.5">
                              <MapPin className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                              <span className="truncate">{locationLabel}</span>
                              {/* Unique viewers — people, not visits. Hidden at 0
                                  so a fresh listing doesn't advertise the fact,
                                  and hidden from buyers entirely: how many rivals
                                  are circling a deal is staff-side information. */}
                              {!isBuyerView && (listing.view_count ?? 0) > 0 && (
                                <span className="flex shrink-0 items-center gap-1" title={`${listing.view_count!.toLocaleString()} unique ${listing.view_count === 1 ? "buyer has" : "buyers have"} viewed this listing`}>
                                  <span className="text-muted-foreground/40">·</span>
                                  <Eye className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                                  {listing.view_count!.toLocaleString()}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span />
                          )}
                          {listing.agent_name && (
                            <span className="flex shrink-0 items-center gap-1.5" title={`BL Advisor: ${listing.agent_name}`}>
                              {listing.agent_avatar_url ? (
                                <img src={assetUrl(listing.agent_avatar_url)!} alt={listing.agent_name} className="h-6 w-6 rounded-full object-cover" />
                              ) : (
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-[0.58rem] font-bold text-primary">{advisorInitials}</span>
                              )}
                              <span className="truncate text-foreground/80">{listing.agent_name}</span>
                            </span>
                          )}
                        </div>

                        {/* Action buttons */}
                        <div className={cn("grid gap-2 pt-1", showMakeOffer ? "grid-cols-2" : "grid-cols-1")}>
                          {/* Make Offer only on active listings — non-active (LOI/Under Offer/Due Diligence/Sold) deals are in progress */}
                          {showMakeOffer && (
                            interestedIds.has(listing.id) ? (
                              <div className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-emerald-500/25 bg-emerald-500/10 py-2.5 text-sm font-semibold text-emerald-400">
                                <CheckCircle className="h-4 w-4" />
                                Interest sent
                              </div>
                            ) : (
                              <button
                                className="flex w-full items-center justify-center rounded-xl border border-primary/60 bg-transparent py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
                                onClick={(e) => openOffer(listing, e)}>
                                Make offer
                              </button>
                            )
                          )}
                          {lockBlocksUnlock ? (
                            <button disabled
                              className="w-full rounded-xl border border-amber-400/30 bg-amber-400/10 py-2.5 text-sm font-semibold text-amber-300">
                              Temporarily locked
                            </button>
                          ) : quotaExhausted ? (
                            <button
                              className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/40 bg-primary/10 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/20"
                              onClick={() => handleQuotaExhausted(listing)}>
                              {isBasicPlan ? "Upgrade to unlock" : `Unlock for $${extraListingAmount ?? "?"}`}
                              <ArrowRight className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/60 bg-gold-gradient py-2.5 text-sm font-semibold text-primary-foreground transition-[filter] hover:brightness-[1.06]"
                              onClick={() => handleViewDetails(listing.id)}>
                              {isSeen ? "View again" : "View details"}
                              <ArrowRight className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ── Table view ── */
              <div className="rounded-2xl border border-border/80 bg-card shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/70 bg-muted/40">
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">BL Code</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Title</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Asking Price</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Revenue</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">EBITDA</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">CF Mult</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Location</th>
                        {!isBuyerView && (
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Views</th>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">BL Advisor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50">
                      {listings.map((listing) => {
                        const locationLabel = listingLocation(listing);
                        const showNewBadge = Boolean(
                          isAdminOrAgent && listing.created_at && (!listingSeenAt || new Date(listing.created_at) > new Date(listingSeenAt))
                        );
                        const { lockBlocksUnlock, quotaExhausted } = listingAction(listing);
                        const isSeen = !isOperator && unlockedListingIds.has(listing.id);
                        const cf = listing.asking_price && listing.ebitda && listing.ebitda !== 0
                          ? (listing.asking_price / listing.ebitda).toFixed(2) + "X"
                          : null;

                        return (
                          <tr
                            key={listing.id}
                            className={cn("group hover:bg-muted/30 transition-colors", isSeen && "bg-slate-50/60")}
                          >
                            <td className="px-4 py-3.5 whitespace-nowrap">
                              {listing.reference_code ? (
                                <span className="font-mono text-xs font-bold tracking-wider text-primary">
                                  {listing.reference_code}
                                </span>
                              ) : (
                                <span className="text-muted-foreground/50">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3.5 max-w-[280px]">
                              <div className="flex items-center gap-1.5">
                                {/* Title is the link — locked deals don't navigate,
                                    exhausted quota routes to the paid-unlock page. */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (lockBlocksUnlock) return;
                                    if (quotaExhausted) { handleQuotaExhausted(listing); return; }
                                    handleViewDetails(listing.id);
                                  }}
                                  title={lockBlocksUnlock ? "Temporarily locked by another buyer" : undefined}
                                  className={cn(
                                    "truncate text-left font-semibold text-foreground underline-offset-2",
                                    lockBlocksUnlock ? "cursor-default" : "hover:text-primary hover:underline",
                                  )}
                                >
                                  {user ? listing.title : categoryName(listing.industry_category_id)}
                                </button>
                                {/* Quick-preview trigger — the hover card opens from
                                    this eye instead of the whole row. Teal = viewed. */}
                                <span
                                  onMouseEnter={(e) => handleRowMouseEnter(listing, e)}
                                  onMouseLeave={handleRowMouseLeave}
                                  title={isSeen ? "Already viewed — hover for preview" : "Hover for preview"}
                                  className="shrink-0 cursor-pointer"
                                >
                                  <Eye className={cn("h-3.5 w-3.5 transition-colors", isSeen ? "text-teal-500" : "text-muted-foreground hover:text-primary")} />
                                </span>
                                <ListingHotBadge published_at={listing.published_at} size="sm" />
                                {!getListingHotBadge(listing.published_at) && showNewBadge && (
                                  <span className="shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[0.58rem] font-bold uppercase tracking-[0.1em] text-primary-foreground">
                                    New
                                  </span>
                                )}
                                {lockBlocksUnlock && <Lock className="shrink-0 h-3.5 w-3.5 text-amber-500" />}
                              </div>
                            </td>
                            <td className="px-4 py-3.5 whitespace-nowrap">
                              <span className="inline-flex items-center gap-1 font-semibold text-primary">
                                <DollarSign className="h-3.5 w-3.5" />
                                {formatPrice(listing.asking_price)}
                              </span>
                            </td>
                            <td className="px-4 py-3.5 whitespace-nowrap text-muted-foreground">
                              {listing.revenue ? (
                                <span className="inline-flex items-center gap-1 text-foreground">
                                  <TrendingUp className="h-3.5 w-3.5 text-primary/70" />
                                  {formatPrice(listing.revenue)}
                                </span>
                              ) : (
                                <span className="text-muted-foreground/50">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3.5 whitespace-nowrap">
                              {listing.ebitda ? (
                                <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                                  {formatPrice(listing.ebitda)}
                                </span>
                              ) : (
                                <span className="text-muted-foreground/50">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3.5 whitespace-nowrap">
                              {cf ? (
                                <span className="font-semibold text-foreground">{cf}</span>
                              ) : (
                                <span className="text-muted-foreground/50">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3.5 whitespace-nowrap">
                              {locationLabel ? (
                                <span className="inline-flex items-center gap-1 text-muted-foreground">
                                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                                  {locationLabel}
                                </span>
                              ) : (
                                <span className="text-muted-foreground/50">—</span>
                              )}
                            </td>
                            {/* Unique viewers — people, not visits. An em dash
                                rather than a 0 keeps the column quiet for
                                listings nobody has opened yet. Buyers don't get
                                this column at all — drop the header with it. */}
                            {!isBuyerView && (
                              <td className="px-4 py-3.5 whitespace-nowrap">
                                {(listing.view_count ?? 0) > 0 ? (
                                  <span className="inline-flex items-center gap-1 text-muted-foreground"
                                    title={`${listing.view_count!.toLocaleString()} unique ${listing.view_count === 1 ? "buyer has" : "buyers have"} viewed this listing`}>
                                    <Eye className="h-3.5 w-3.5 shrink-0" />
                                    {listing.view_count!.toLocaleString()}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground/50">—</span>
                                )}
                              </td>
                            )}
                            <td className="px-4 py-3.5">
                              {listing.agent_name ? (
                                <div className="flex items-center gap-1.5">
                                  {listing.agent_avatar_url ? (
                                    <img src={assetUrl(listing.agent_avatar_url)!} alt={listing.agent_name} className="h-6 w-6 rounded-full object-cover" />
                                  ) : (
                                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
                                      <Users className="h-3 w-3" />
                                    </div>
                                  )}
                                  <span className="text-xs text-muted-foreground whitespace-nowrap">{listing.agent_name}</span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground/50">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pagination (Active + Unlocks; the Coming Soon tab paginates itself) */}
            {marketTab !== "coming_soon" && !loading && totalPages > 1 && (
              <Pagination
                page={currentPage}
                totalElements={total}
                pageSize={pageSize}
                onChange={fetchListings}
                className="mt-6"
              />
            )}
          </div>
        </section>
      )}
      {/* Table row hover card */}
      {hoveredListing && (
        <div
          ref={hoverCardRef}
          onMouseEnter={() => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); }}
          onMouseLeave={() => setHoveredListing(null)}
          style={{ top: hoverCardPos.top, left: hoverCardPos.left, width: 340 }}
          className="fixed z-50 rounded-2xl border border-border/80 bg-card shadow-xl overflow-hidden transition-opacity duration-150"
        >
          {/* Header */}
          <div className="bg-primary/10 px-4 py-3 border-b border-border/60">
            <span className="inline-flex items-center rounded-full border border-primary/20 bg-white/70 px-2.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-primary">
              {categoryName(hoveredListing.industry_category_id)}
            </span>
            <p className="mt-1.5 font-bold text-foreground leading-snug line-clamp-2">
              {user ? hoveredListing.title : categoryName(hoveredListing.industry_category_id)}
            </p>
          </div>

          <div className="px-4 py-3 space-y-3">
            {/* Description */}
            {(() => {
              const desc = isAdminOrAgent ? hoveredListing.overview : (hoveredListing.display_overview || hoveredListing.overview);
              return desc ? (
                <p className="text-xs leading-5 text-muted-foreground line-clamp-3">{desc}</p>
              ) : null;
            })()}

            {/* Price metrics */}
            {(() => {
              const cfMult = hoveredListing.asking_price && hoveredListing.ebitda && hoveredListing.ebitda !== 0
                ? (hoveredListing.asking_price / hoveredListing.ebitda).toFixed(2) + "X"
                : null;
              return (
                <div className={`grid gap-2 ${cfMult ? "grid-cols-2" : "grid-cols-3"}`}>
                  <div className="rounded-lg border border-border/50 bg-primary/10 px-2 py-1.5 text-center">
                    <div className="text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Asking</div>
                    <div className="mt-0.5 text-xs font-bold text-primary truncate">{formatPrice(hoveredListing.asking_price)}</div>
                  </div>
                  <div className="rounded-lg border border-border/50 bg-primary/10 px-2 py-1.5 text-center">
                    <div className="text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Revenue</div>
                    <div className="mt-0.5 text-xs font-bold text-foreground truncate">{hoveredListing.revenue ? formatPrice(hoveredListing.revenue) : "—"}</div>
                  </div>
                  <div className="rounded-lg border border-border/50 bg-primary/10 px-2 py-1.5 text-center">
                    <div className="text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">EBITDA</div>
                    <div className="mt-0.5 text-xs font-bold text-foreground truncate">{hoveredListing.ebitda ? formatPrice(hoveredListing.ebitda) : "—"}</div>
                  </div>
                  {cfMult && (
                    <div className="rounded-lg border border-border/50 bg-primary/10 px-2 py-1.5 text-center">
                      <div className="text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">C.F. Multiple</div>
                      <div className="mt-0.5 text-xs font-bold text-foreground">{cfMult}</div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Location + Established */}
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {(() => {
                const loc = listingLocation(hoveredListing);
                return loc ? (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    {loc}
                  </span>
                ) : null;
              })()}
              {hoveredListing.established_year && (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="h-3.5 w-3.5 shrink-0" />
                  Est. {hoveredListing.established_year}
                </span>
              )}
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-1.5">
              {hoveredListing.is_sba_approved && (
                <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-[0.62rem] font-semibold text-emerald-500">
                  SBA Approved
                </span>
              )}
              {hoveredListing.website_url && (
                <a
                  href={hoveredListing.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-[0.62rem] font-semibold text-muted-foreground hover:text-primary transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  Website ↗
                </a>
              )}
            </div>

            {/* Advisor */}
            {hoveredListing.agent_name && (
              <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/25 px-3 py-2">
                {hoveredListing.agent_avatar_url ? (
                  <img src={assetUrl(hoveredListing.agent_avatar_url)!} alt={hoveredListing.agent_name} className="h-6 w-6 rounded-full object-cover" />
                ) : (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Users className="h-3 w-3" />
                  </div>
                )}
                <span className="text-xs text-muted-foreground">
                  BL Advisor: <span className="font-semibold text-foreground">{hoveredListing.agent_name}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>

      {/* Paid-unlock checkout popup — one click on the saved card, or a full card form */}
      {paidUnlockListing && (
        <UnlockListingModal
          listingId={paidUnlockListing.id}
          listingTitle={paidUnlockListing.title}
          onClose={() => setPaidUnlockListing(null)}
          onUnlocked={(listingId) => {
            setPaidUnlockListing(null);
            setUnlockedListingIds((prev) => new Set([...prev, listingId]));
            navigate(`/listings/${listingId}`);
          }}
        />
      )}

      {/* Unlock quota warning popup — appears when ≤3 unlocks remain, user must click OK to proceed */}
      {unlockPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 flex max-w-sm flex-col gap-4 rounded-2xl border border-amber-500/40 bg-card px-6 py-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/15">
                <Zap className="h-4 w-4 text-amber-500" />
              </span>
              <p className="text-sm font-semibold leading-snug text-foreground">{unlockPopup}</p>
            </div>
            <div className="flex gap-2">
              <Button
                className="flex-1"
                onClick={() => {
                  const listingId = pendingNavigate;
                  setUnlockPopup(null);
                  setPendingNavigate(null);
                  // doUnlock navigates only once the unlock succeeds, so a 409
                  // (locked) or 402 (quota) surfaces here instead of on a page
                  // the buyer has already left for.
                  if (listingId) doUnlock(listingId);
                }}
              >
                OK
              </Button>
              <Button
                variant="outline"
                onClick={() => { setUnlockPopup(null); setPendingNavigate(null); }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Out of free unlocks (Basic) — the only way on is a plan, so this popup
          replaces the paid-unlock checkout subscribers would see here. */}
      {upgradePopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => setUpgradePopup(null)}>
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mt-4 text-lg font-extrabold text-foreground">Out of free unlocks</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{upgradePopup}</p>
            <Button
              className="mt-5 w-full"
              onClick={() => {
                setUpgradePopup(null);
                navigate("/choose-plan");
              }}
            >
              See plans
              <ArrowRight className="h-4 w-4" />
            </Button>
            <button
              onClick={() => setUpgradePopup(null)}
              className="mt-3 text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Make Offer / Show Interest popup — buyers express interest directly from a listing card */}
      {offerListing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => !offerSubmitting && setOfferListing(null)}>
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-extrabold text-gray-900">Show Interest</h3>
                <p className="mt-0.5 line-clamp-2 text-sm text-gray-500">{offerListing.title}</p>
              </div>
              <button onClick={() => !offerSubmitting && setOfferListing(null)}
                className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 space-y-2">
              <p className="text-[0.6rem] font-bold uppercase tracking-widest text-gray-400">
                Your Message <span className="normal-case tracking-normal font-medium">(optional)</span>
              </p>
              <Textarea rows={3} placeholder="e.g. preferred visit time, questions…"
                value={offerMessage} onChange={(e) => setOfferMessage(e.target.value)}
                className="resize-none text-sm" />
            </div>

            <div className="mt-5 flex gap-2">
              <button onClick={submitOffer} disabled={offerSubmitting}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-primary/60 bg-gold-gradient py-2.5 text-sm font-semibold text-primary-foreground transition-[filter] hover:brightness-[1.06] disabled:opacity-60">
                {offerSubmitting ? "Sending…" : "Confirm"}
              </button>
              <button onClick={() => setOfferListing(null)} disabled={offerSubmitting}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
